import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  CreditCard,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
} from "lucide-react";
import api from "../../utils/api";

// Komponenty UI
import Button from "../ui/Button";
import Loader from "../ui/Loader";
import ErrorState from "../ui/ErrorState";
import Pagination from "../ui/Pagination";
import FilterBar from "../ui/FilterBar";
import SearchableSelect from "../ui/SearchableSelect";
import Select from "../ui/Select";
import SearchBar from "../ui/SearchBar";
import CsvExportButton from "../ui/CsvExportButton";

const AdminStripeTransactions = () => {
  const { t, i18n } = useTranslation("admin");
  const lang = i18n.language || "pl"; // <-- POBIERAMY JĘZYK

  // --- STANY ---
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  // --- FILTROWANIE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const [filterStatus, setFilterStatus] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // --- HELPER DLA NAZWY PROJEKTU ---
  const getProjectTitle = (tItem) => {
    if (tItem.isGeneral) return t("stripeTransactions.generalDonation");
    if (tItem.projectTitleObj) {
      return (
        tItem.projectTitleObj[lang] ||
        tItem.projectTitleObj["pl"] ||
        tItem.projectTitleObj["en"] ||
        "Untitled"
      );
    }
    return tItem.description || "Untitled";
  };

  // --- POBIERANIE DANYCH ---
  const fetchTransactions = async () => {
    setLoading(true);
    setPageError(null);
    try {
      const res = await api.get("/payments/transactions");
      setTransactions(res.data || []);
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setPageError(t("stripeTransactions.errors.fetchTransactions"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // --- OPCJE FILTRÓW ---
  const currencyOptions = [
    { value: "PLN", label: "PLN" },
    { value: "EUR", label: "EUR" },
  ];

  const statusOptions = [
    { value: "succeeded", label: t("stripeTransactions.status.succeeded") },
    { value: "failed", label: t("stripeTransactions.status.failed") },
    { value: "pending", label: t("stripeTransactions.status.pending") },
  ];

  const projectOptions = useMemo(() => {
    const uniqueProjects = new Set();
    transactions.forEach((t) => {
      uniqueProjects.add(getProjectTitle(t));
    });
    return Array.from(uniqueProjects).map((title) => ({
      value: title,
      label: title,
    }));
  }, [transactions, lang, t]); // Zależność od lang i t

  // --- PRZETWARZANIE DANYCH ---
  const processedTransactions = useMemo(() => {
    let result = [...transactions];

    if (filterStatus) {
      result = result.filter((t) => t.status === filterStatus);
    }
    if (filterCurrency) {
      result = result.filter((t) => t.currency === filterCurrency);
    }
    if (filterProject) {
      // Porównujemy przetłumaczoną nazwę!
      result = result.filter((t) => getProjectTitle(t) === filterProject);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((t) => {
        const title = getProjectTitle(t).toLowerCase();
        return t.customer.toLowerCase().includes(term) || title.includes(term);
      });
    }

    result.sort((a, b) => {
      let valA, valB;
      if (sortBy === "amount") {
        valA = parseFloat(a.amount || 0);
        valB = parseFloat(b.amount || 0);
      } else {
        valA = new Date(a.date).getTime();
        valB = new Date(b.date).getTime();
      }
      return sortOrder === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
          ? 1
          : -1;
    });

    return result;
  }, [
    transactions,
    filterStatus,
    filterCurrency,
    filterProject,
    searchTerm,
    sortBy,
    sortOrder,
    lang, // Zależność
    t, // Zależność
  ]);

  const totalPages = Math.ceil(processedTransactions.length / itemsPerPage);
  const paginatedTransactions = processedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterStatus,
    filterCurrency,
    filterProject,
    searchTerm,
    sortBy,
    sortOrder,
    lang,
  ]);

  // --- HELPERY UI ---
  const renderStatusBadge = (status) => {
    switch (status) {
      case "succeeded":
        return (
          <span className="stripe-badge success">
            <CheckCircle size={14} /> Succeeded
          </span>
        );
      case "failed":
        return (
          <span className="stripe-badge danger">
            <XCircle size={14} /> Failed
          </span>
        );
      default:
        return (
          <span className="stripe-badge warning">
            <Clock size={14} /> Pending
          </span>
        );
    }
  };

  return (
    <div className="admin-stripe-transactions">
      <header className="page-header">
        <div className="header-content">
          <h1 className="page-title">{t("stripeTransactions.title")}</h1>
          <p className="page-subtitle">{t("stripeTransactions.subtitle")}</p>
        </div>

        {!pageError && !loading && (
          <div className="header-actions">
            {/* ZMIANA: Przekazujemy lang do endpointu CSV */}
            <CsvExportButton
              filenamePrefix="stripe_transactions"
              exportUrl={`/payments/transactions/export?lang=${lang}`}
            />

            <Button
              variant="outline"
              icon={<RefreshCw size={18} />}
              onClick={fetchTransactions}
            >
              {t("stripeTransactions.buttons.refresh")}
            </Button>

            <a
              href="https://dashboard.stripe.com/acct_1SlzALKyk8P4m3gY/payments"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Button variant="primary" icon={<ExternalLink size={18} />}>
                {t("stripeTransactions.buttons.openStripe")}
              </Button>
            </a>
          </div>
        )}
      </header>

      {/* GŁÓWNA ZAWARTOŚĆ */}
      {loading ? (
        <div className="loader-container">
          <Loader />
        </div>
      ) : pageError ? (
        <ErrorState message={pageError} onRetry={fetchTransactions} />
      ) : (
        <>
          {/* PASEK FILTRÓW */}
          <FilterBar
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={setSortBy}
            onOrderToggle={() =>
              setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
            }
            onClear={() => {
              setFilterStatus("");
              setFilterCurrency("");
              setFilterProject("");
              setSearchTerm("");
              setSortBy("date");
            }}
            sortOptions={[
              {
                value: "date",
                label: t("stripeTransactions.filters.sortOptions.date"),
              },
              {
                value: "amount",
                label: t("stripeTransactions.filters.sortOptions.amount"),
              },
            ]}
          >
            <div className="filter-search">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                onClear={() => setSearchTerm("")}
                placeholder={t("stripeTransactions.searchPlaceholder")}
              />
            </div>

            <div className="filter-project">
              <SearchableSelect
                options={[
                  {
                    value: "",
                    label: t("stripeTransactions.filters.allProjects"),
                  },
                  ...projectOptions,
                ]}
                value={filterProject}
                onChange={(val) => setFilterProject(val)}
                placeholder={t("stripeTransactions.filters.filterByProject")}
              />
            </div>

            <div className="filter-currency">
              <Select
                value={filterStatus}
                onChange={(val) => setFilterStatus(val)}
                options={[
                  {
                    value: "",
                    label: t("stripeTransactions.status.allStatuses"),
                  },
                  ...statusOptions,
                ]}
                placeholder={t("stripeTransactions.filters.status")}
              />
            </div>

            <div className="filter-currency">
              <Select
                value={filterCurrency}
                onChange={(val) => setFilterCurrency(val)}
                options={[
                  {
                    value: "",
                    label: t("stripeTransactions.filters.allCurrencies"),
                  },
                  ...currencyOptions,
                ]}
                placeholder={t("stripeTransactions.filters.currency")}
              />
            </div>
          </FilterBar>

          {/* TABELA DANYCH */}
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t("stripeTransactions.table.amount")}</th>
                  <th>{t("stripeTransactions.table.currency")}</th>
                  <th>{t("stripeTransactions.table.status")}</th>
                  <th>{t("stripeTransactions.table.method")}</th>
                  <th>{t("stripeTransactions.table.project")}</th>
                  <th>{t("stripeTransactions.table.customer")}</th>
                  <th>{t("stripeTransactions.table.date")}</th>
                  <th>{t("stripeTransactions.table.details")}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.length === 0 ? (
                  <tr className="empty-state-row">
                    <td colSpan="8">{t("stripeTransactions.emptyState")}</td>
                  </tr>
                ) : (
                  paginatedTransactions.map((tItem) => {
                    const amountClass =
                      tItem.status === "succeeded"
                        ? "col-amount-success"
                        : "col-amount-muted";

                    // --- FUNKCJA TŁUMACZĄCA METODĘ PŁATNOŚCI ---
                    // Próbujemy znaleźć tłumaczenie dla danego słowa, jeśli nie ma, zostawiamy oryginał
                    const translatedMethod = t(
                      `stripeTransactions.methods.${tItem.paymentMethod}`,
                      { defaultValue: tItem.paymentMethod },
                    );

                    // --- FUNKCJA TŁUMACZĄCA POWÓD ODRZUCENIA ---
                    const translatedDeclineReason = tItem.declineReason
                      ? t(
                          `stripeTransactions.declineReasons.${tItem.declineReason}`,
                          { defaultValue: tItem.declineReason },
                        )
                      : null;

                    return (
                      <tr
                        key={tItem.id}
                        className={
                          tItem.status === "failed" ? "row-failed" : ""
                        }
                      >
                        <td className={`col-amount ${amountClass}`}>
                          {parseFloat(tItem.amount).toFixed(2)}
                        </td>
                        <td className="col-currency">{tItem.currency}</td>
                        <td>{renderStatusBadge(tItem.status)}</td>
                        <td>
                          <div className="payment-method">
                            <CreditCard size={14} className="method-icon" />
                            <span>{translatedMethod}</span>
                          </div>
                        </td>
                        <td>
                          <span className="cell-highlight">
                            {getProjectTitle(tItem)}
                          </span>
                        </td>
                        <td className="col-customer">{tItem.customer}</td>
                        <td>
                          <div className="cell-date">
                            <Calendar size={14} />
                            {new Date(tItem.date).toLocaleString()}
                          </div>
                        </td>
                        <td className="col-details">
                          {translatedDeclineReason ? (
                            <span
                              className="error-reason"
                              title={translatedDeclineReason}
                            >
                              {translatedDeclineReason}
                            </span>
                          ) : (
                            <span className="empty-dash">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AdminStripeTransactions;
